<script lang="ts" setup>
import { Pencil } from 'lucide-vue-next';
import { computed } from 'vue';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Member } from '@/stores/access-management';

import EditRoleDialog from './dialogs/EditRoleDialog.vue';
import RemoveMemberDialog from './dialogs/RemoveMemberDialog.vue';

interface Props {
  members: Member[];
  orgId: string;
  currentUserId?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  roleUpdated: [member: Member, newRole: string];
  memberRemoved: [member: Member];
}>();

// Filter out current user from member list
const otherMembers = computed(() => {
  if (!props.currentUserId) {
    return props.members;
  }
  return props.members.filter(m => m.userId !== props.currentUserId);
});

function getRoleBadgeVariant(role: string) {
  switch (role) {
    case 'OWNER':
      return 'default';
    case 'ADMIN':
      return 'default';
    case 'MEMBER':
      return 'secondary';
    case 'VIEWER':
      return 'outline';
    default:
      return 'secondary';
  }
}

function handleRoleUpdate(member: Member, newRole: string) {
  emit('roleUpdated', member, newRole);
}

function handleMemberRemove(member: Member) {
  emit('memberRemoved', member);
}
</script>

<template>
  <div>
    <div v-if="otherMembers.length === 0" class="text-center py-8 text-muted-foreground text-sm border rounded-md">
      No other members in this organization. Invite someone to collaborate!
    </div>

    <div v-else class="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead class="w-[300px]">Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead class="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-for="member in otherMembers" :key="member.userId">
            <TableCell>
              <span class="font-medium">{{ member.email }}</span>
            </TableCell>
            <TableCell>
              <Badge :variant="getRoleBadgeVariant(member.role)">
                {{ member.role }}
              </Badge>
            </TableCell>
            <TableCell class="text-right">
              <div class="flex items-center justify-end gap-2">
                <EditRoleDialog
                  :member="member"
                  :org-id="orgId"
                  @role-updated="handleRoleUpdate"
                >
                  <template #trigger>
                    <Button variant="ghost" size="sm" class="h-8 w-8 p-0">
                      <Pencil class="h-4 w-4" />
                    </Button>
                  </template>
                </EditRoleDialog>

                <RemoveMemberDialog
                  :member="member"
                  :org-id="orgId"
                  @confirmed="handleMemberRemove"
                >
                  <template #trigger>
                    <Button variant="ghost" size="sm">
                      Remove
                    </Button>
                  </template>
                </RemoveMemberDialog>
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  </div>
</template>
